/**
 * Interactive SVG trend graph for a selected laboratory analyte.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CalendarRange, MousePointer2, RotateCcw } from 'lucide-react';
import type { LabResultItem } from '../../../types';
import { formatDisplayDate } from '../../../utils/displayDate';
import { DateRangeCalendar } from '../../../components/DateRangeCalendar/DateRangeCalendar';
import { LabTrendValueTable } from './LabTrendValueTable';
import {
  buildLinePath,
  buildTrendSummary,
  classifyValue,
  filterHistoryIndicesByRange,
  formatAxisMonthLabel,
  formatRangeLabel,
  getDataExtent,
  getDefaultDateRange,
  getKeyPointIndices,
  getNiceTicks,
  getSelectableBounds,
  getTimeAxisTicks,
  parseReferenceRange,
  rangeToTimestamps,
  statusLabel,
  toTimestamp,
} from './labTrendUtils';

interface LabTrendChartProps {
  item: LabResultItem;
}

const WIDTH = 720;
const HEIGHT = 284;
const PADDING = { top: 32, right: 40, bottom: 36, left: 64 };
const DATE_LABEL_Y = HEIGHT - 16;

/**
 * Render the reference trend line, normal range, hover detail, and value table
 * for the readings inside a user-selected date range. A calendar popover lets
 * the clinician pick any custom range; the view defaults to the most recent
 * month of data and resets when the analyte changes.
 *
 * @param props - Selected analyte with chronological history.
 * @returns Interactive trend visualization.
 */
export function LabTrendChart({ item }: LabTrendChartProps) {
  const [hoveredPoint, setHoveredPoint] = useState<number | null>(null);
  const [dateRange, setDateRange] = useState<{ start: Date; end: Date } | null>(
    () => getDefaultDateRange(item.history),
  );
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement | null>(null);
  const chartWidth = WIDTH - PADDING.left - PADDING.right;
  const chartHeight = HEIGHT - PADDING.top - PADDING.bottom;
  const range = parseReferenceRange(item.referenceRange);

  const dataExtent = useMemo(() => getDataExtent(item.history), [item.history]);
  // Days outside the first→last reading span are greyed/unclickable in the picker.
  const selectableBounds = useMemo(() => getSelectableBounds(item.history), [item.history]);

  // Reset the visible range (and close the picker) when the analyte changes —
  // this component instance is reused across analytes with no `key`.
  useEffect(() => {
    setDateRange(getDefaultDateRange(item.history));
    setIsPickerOpen(false);
  }, [item.key, item.history]);

  // Close the picker popover on an outside click.
  useEffect(() => {
    if (!isPickerOpen) return undefined;
    const handleClick = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        setIsPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isPickerOpen]);

  // Timestamp bounds of the active range (falls back to the full data extent).
  const activeRange = useMemo(() => {
    if (dateRange) return rangeToTimestamps(dateRange.start, dateRange.end);
    if (dataExtent) return { startTs: dataExtent.earliestTs, endTs: dataExtent.latestTs };
    return { startTs: 0, endTs: 0 };
  }, [dateRange, dataExtent]);

  const visibleIndices = useMemo(
    () => filterHistoryIndicesByRange(item.history, activeRange),
    [item.history, activeRange],
  );

  const points = useMemo(
    () => visibleIndices.map((index) => item.history[index]),
    [visibleIndices, item.history],
  );

  const isDefaultRange = useMemo(() => {
    const fallback = getDefaultDateRange(item.history);
    if (!fallback || !dateRange) return !dateRange;
    return (
      fallback.start.getTime() === dateRange.start.getTime() &&
      fallback.end.getTime() === dateRange.end.getTime()
    );
  }, [item.history, dateRange]);

  const handleRangeChange = useCallback((start: Date | null, end: Date | null) => {
    if (!start || !end) return;
    setDateRange({ start, end });
    setIsPickerOpen(false);
  }, []);

  const handleResetRange = useCallback(() => {
    setDateRange(getDefaultDateRange(item.history));
    setIsPickerOpen(false);
  }, [item.history]);

  const applyQuickRange = useCallback((next: { start: Date; end: Date }) => {
    setDateRange(next);
    setIsPickerOpen(false);
  }, []);

  // Quick range presets: last N months plus "All". Month presets that would
  // reach past the first reading are dropped (they'd duplicate "All").
  const quickRanges = useMemo(() => {
    if (!selectableBounds) return [];
    const presets: Array<{ id: string; label: string; range: { start: Date; end: Date } }> = [];
    [2, 4, 6].forEach((months) => {
      const range = getDefaultDateRange(item.history, months);
      if (range && range.start.getTime() > selectableBounds.min.getTime()) {
        presets.push({ id: `${months}m`, label: `${months}M`, range });
      }
    });
    presets.push({
      id: 'all',
      label: 'All',
      range: { start: selectableBounds.min, end: selectableBounds.max },
    });
    return presets;
  }, [item.history, selectableBounds]);

  const isRangeActive = useCallback(
    (candidate: { start: Date; end: Date }) =>
      !!dateRange &&
      dateRange.start.getTime() === candidate.start.getTime() &&
      dateRange.end.getTime() === candidate.end.getTime(),
    [dateRange],
  );

  // Clear hover whenever the analyte or the visible range changes.
  useEffect(() => setHoveredPoint(null), [item.key, activeRange.startTs, activeRange.endTs]);

  const timestamps = useMemo(() => points.map((point) => toTimestamp(point.date)), [points]);
  // Domain spans the selected range so the cadence stays honest across markers.
  const domain = useMemo(() => ({ start: activeRange.startTs, end: activeRange.endTs }), [activeRange]);

  const yAxis = useMemo(() => {
    const values = points.map((point) => point.value);
    if (range.minimum !== null) values.push(range.minimum);
    if (range.maximum !== null) values.push(range.maximum);
    return getNiceTicks(Math.min(...values), Math.max(...values), 6);
  }, [points, range.maximum, range.minimum]);

  const bounds = { minimum: yAxis.lower, maximum: yAxis.upper };
  // Derive label precision from the tick step so narrow-range analytes (e.g.
  // 0.02–0.05) don't collapse to duplicate "0.0" labels.
  const yPrecision = Math.max(0, -Math.floor(Math.log10(yAxis.step)));
  const xTicks = useMemo(
    () => getTimeAxisTicks(domain.start, domain.end),
    [domain.start, domain.end],
  );

  const xScale = (index: number) => {
    if (points.length <= 1) return PADDING.left + chartWidth / 2;
    const ts = timestamps[index];
    if (domain.end > domain.start && Number.isFinite(ts)) {
      return PADDING.left + ((ts - domain.start) / (domain.end - domain.start)) * chartWidth;
    }
    return PADDING.left + (index / Math.max(points.length - 1, 1)) * chartWidth;
  };
  const xAtPosition = (position: number) => PADDING.left + position * chartWidth;
  const yScale = (value: number) =>
    PADDING.top +
    chartHeight -
    ((value - bounds.minimum) / (bounds.maximum - bounds.minimum)) * chartHeight;
  const path = buildLinePath(points, xScale, yScale);

  const statuses = useMemo(() => points.map((point) => classifyValue(point.value, range)), [points, range]);
  const latestStatus = statuses.length ? statuses[statuses.length - 1] : 'unknown';
  const latestStatusWord = latestStatus === 'unknown' ? item.status.toLowerCase() : latestStatus;
  const summary = buildTrendSummary(points, item.label, item.unit, latestStatusWord);

  const hasPoints = points.length > 0;
  const hasReference = range.minimum !== null || range.maximum !== null;
  const hasLow = statuses.includes('low');
  const hasHigh = statuses.includes('high');
  const keyPoints = useMemo(() => getKeyPointIndices(points), [points]);
  const latestIndex = points.length - 1;
  const rangeLabel = dateRange ? formatRangeLabel(dateRange.start, dateRange.end) : 'All readings';

  // Guard against a hovered index that no longer exists in the current window.
  const activePoint =
    hoveredPoint !== null && hoveredPoint < points.length ? hoveredPoint : null;

  /**
   * Render the hover detail card (date, value, status) for a reading.
   *
   * @param index - Index of the hovered measurement within the current window.
   * @returns The positioned SVG tooltip group.
   */
  const renderTooltip = (index: number) => {
    const point = points[index];
    const x = xScale(index);
    const y = yScale(point.value);
    const status = statuses[index];
    const statusText = statusLabel(status, range, item.unit);
    const width = 158;
    const height = statusText ? 54 : 40;
    const tooltipX = Math.min(Math.max(x - width / 2, 8), WIDTH - width - 8);
    const tooltipY = Math.max(y - height - 12, 8);
    const centerX = tooltipX + width / 2;
    return (
      <g className="trend-tooltip">
        <rect x={tooltipX} y={tooltipY} width={width} height={height} rx={6} />
        <text className="trend-tooltip-value" x={centerX} y={tooltipY + 19}>
          {point.value} {item.unit}
        </text>
        <text className="trend-tooltip-date" x={centerX} y={tooltipY + 33}>
          {formatDisplayDate(point.date)}
        </text>
        {statusText && (
          <text
            className={`trend-tooltip-status trend-tooltip-status--${status}`}
            x={centerX}
            y={tooltipY + 47}
          >
            {statusText}
          </text>
        )}
      </g>
    );
  };

  return (
    <div className="trend-plot-container" data-testid="lab-trend-chart">
      <div className="trend-plot-header">
        <span className="trend-plot-title">{item.label} Trend</span>
        <span className="trend-plot-subtitle">
          {item.history.length} measurement{item.history.length === 1 ? '' : 's'} · Reference{' '}
          {item.referenceRange || 'not reported'}
        </span>
      </div>

      <div className="trend-plot-body">
        <div className="trend-plot-chart">
          {!hasPoints && (
            <div className="trend-plot-empty">
              No readings between {rangeLabel}. Adjust the date range to see measurements.
            </div>
          )}
          {hasPoints && (
          <svg
            className="trend-plot-svg"
            viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
            role="img"
            aria-label={`${item.label} laboratory trend`}
          >
            {yAxis.ticks.map((value) => {
              const y = yScale(value);
              return (
                <g key={`ygrid-${value}`}>
                  <line
                    x1={PADDING.left}
                    x2={WIDTH - PADDING.right}
                    y1={y}
                    y2={y}
                    className="trend-grid-line"
                  />
                  <text x={PADDING.left - 10} y={y + 4} className="trend-axis-label">
                    {value.toFixed(yPrecision)}
                  </text>
                </g>
              );
            })}

            <text
              className="trend-axis-title"
              x={16}
              y={PADDING.top + chartHeight / 2}
              transform={`rotate(-90 16 ${PADDING.top + chartHeight / 2})`}
            >
              {item.unit}
            </text>

            {xTicks.map((tick, index) => {
              const x = xAtPosition(tick.position);
              return (
                <g key={`xtick-${index}`}>
                  <line
                    x1={x}
                    x2={x}
                    y1={PADDING.top}
                    y2={PADDING.top + chartHeight}
                    className="trend-grid-line-vertical"
                  />
                  <text x={x} y={DATE_LABEL_Y} className="trend-date-label">
                    {tick.label}
                  </text>
                </g>
              );
            })}

            {xTicks.length === 0 && Number.isFinite(timestamps[0]) && (
              <text
                x={PADDING.left + chartWidth / 2}
                y={DATE_LABEL_Y}
                className="trend-date-label"
              >
                {formatAxisMonthLabel(timestamps[0])}
              </text>
            )}

            {range.minimum !== null && range.maximum !== null && (
              <rect
                x={PADDING.left}
                y={yScale(range.maximum)}
                width={chartWidth}
                height={Math.max(yScale(range.minimum) - yScale(range.maximum), 0)}
                className="trend-reference-band"
              />
            )}
            {range.minimum !== null && (
              <line
                x1={PADDING.left}
                x2={WIDTH - PADDING.right}
                y1={yScale(range.minimum)}
                y2={yScale(range.minimum)}
                className="trend-reference-line"
              />
            )}
            {range.maximum !== null && (
              <line
                x1={PADDING.left}
                x2={WIDTH - PADDING.right}
                y1={yScale(range.maximum)}
                y2={yScale(range.maximum)}
                className="trend-reference-line"
              />
            )}

            {activePoint !== null && (
              <line
                className="trend-crosshair"
                x1={xScale(activePoint)}
                x2={xScale(activePoint)}
                y1={PADDING.top}
                y2={PADDING.top + chartHeight}
              />
            )}

            <path d={path} className="trend-data-line" />

            {points.map((point, index) => {
              const status = statuses[index];
              if (status !== 'low' && status !== 'high') return null;
              return (
                <circle
                  key={`pulse-${point.date}-${index}`}
                  className={`trend-outlier-pulse trend-outlier-pulse--${status}`}
                  cx={xScale(index)}
                  cy={yScale(point.value)}
                  r={4}
                >
                  <animate
                    attributeName="r"
                    values="4;11;4"
                    dur="1.8s"
                    repeatCount="indefinite"
                  />
                  <animate
                    attributeName="opacity"
                    values="0.45;0;0.45"
                    dur="1.8s"
                    repeatCount="indefinite"
                  />
                </circle>
              );
            })}

            {points.map((point, index) => {
              const status = statuses[index];
              const outOfRange = status === 'low' || status === 'high';
              if (activePoint !== index && !keyPoints.has(index)) return null;
              return (
                <circle
                  key={`dot-${point.date}-${index}`}
                  cx={xScale(index)}
                  cy={yScale(point.value)}
                  r={activePoint === index ? 5 : 3.5}
                  className={`trend-data-point${outOfRange ? ` trend-data-point--${status}` : ''}`}
                />
              );
            })}

            <g onMouseLeave={() => setHoveredPoint(null)}>
              {points.map((point, index) => {
                const left =
                  index === 0
                    ? PADDING.left
                    : (xScale(index - 1) + xScale(index)) / 2;
                const right =
                  index === latestIndex
                    ? WIDTH - PADDING.right
                    : (xScale(index) + xScale(index + 1)) / 2;
                return (
                  <rect
                    key={`hit-${point.date}-${index}`}
                    className="trend-point-hit-area"
                    x={left}
                    y={PADDING.top}
                    width={Math.max(right - left, 0)}
                    height={chartHeight}
                    onMouseEnter={() => setHoveredPoint(index)}
                  />
                );
              })}
            </g>

            {activePoint !== null && renderTooltip(activePoint)}
          </svg>
          )}
          {hasPoints && (hasReference || hasLow || hasHigh) && (
            <ul className="trend-legend" aria-label="Chart legend">
              {hasReference && (
                <li className="trend-legend-item">
                  <span className="trend-legend-swatch trend-legend-swatch--normal" aria-hidden="true" />
                  Normal range{item.referenceRange ? ` (${item.referenceRange})` : ''}
                </li>
              )}
              {hasLow && (
                <li className="trend-legend-item">
                  <span className="trend-legend-swatch trend-legend-swatch--low" aria-hidden="true" />
                  Below range
                </li>
              )}
              {hasHigh && (
                <li className="trend-legend-item">
                  <span className="trend-legend-swatch trend-legend-swatch--high" aria-hidden="true" />
                  Above range
                </li>
              )}
            </ul>
          )}
          {hasPoints && (
            <p className="trend-plot-hint">
              <MousePointer2 className="trend-plot-hint-icon" size={12} aria-hidden="true" />
              Hover across the line to see details for any reading
            </p>
          )}
        </div>
        <div className="trend-plot-side">
          {dataExtent && (
            <div className="trend-range-controls">
            <div className="trend-range-picker" ref={pickerRef}>
              <button
                type="button"
                className="trend-range-trigger"
                onClick={() => setIsPickerOpen((open) => !open)}
                aria-haspopup="dialog"
                aria-expanded={isPickerOpen}
                aria-label={`Change date range (currently ${rangeLabel})`}
              >
                <CalendarRange className="trend-range-trigger-icon" size={14} aria-hidden="true" />
                <span className="trend-range-trigger-label">{rangeLabel}</span>
              </button>
              {!isDefaultRange && (
                <button
                  type="button"
                  className="trend-range-reset"
                  onClick={handleResetRange}
                  aria-label="Reset to the most recent readings"
                  title="Reset to the most recent readings"
                >
                  <RotateCcw size={13} aria-hidden="true" />
                </button>
              )}
              {isPickerOpen && (
                <div className="trend-range-popover" role="dialog" aria-label="Select date range">
                  <DateRangeCalendar
                    initialStartDate={dateRange?.start}
                    initialEndDate={dateRange?.end}
                    minDate={selectableBounds?.min}
                    maxDate={selectableBounds?.max}
                    onDateRangeChange={handleRangeChange}
                  />
                </div>
              )}
            </div>
            {quickRanges.length > 0 && (
              <div className="trend-range-presets" role="group" aria-label="Quick date ranges">
                {quickRanges.map((preset) => (
                  <button
                    key={preset.id}
                    type="button"
                    className={`trend-range-preset${
                      isRangeActive(preset.range) ? ' trend-range-preset--active' : ''
                    }`}
                    onClick={() => applyQuickRange(preset.range)}
                    aria-pressed={isRangeActive(preset.range)}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            )}
            </div>
          )}
          <LabTrendValueTable
            points={points}
            unit={item.unit}
            label={item.label}
            activeIndex={activePoint}
            onSelectIndex={setHoveredPoint}
          />
        </div>
      </div>
      <div className="trend-summary">
        <ul className="trend-summary-list">
          {summary.map((line) => (
            <li key={line} className="trend-summary-item">{line}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export default LabTrendChart;
